import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  Card,
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Button,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
} from "reactstrap";

// import images
import bgImage from "../../assets/images/bgImage1.jpg";
import { useQueryClient } from "@tanstack/react-query";
import useUserRegistration from "./useUserRegistration";
import { fetchInitDataFn } from "./useIniDataFromDb";

const Register = () => {
  document.title = "Title Appraiser | User Registration";
  const navigate = useNavigate();

  const [orgExistsModal, setOrgExistsModal] = useState(false);

  const {isPending,registerUser} = useUserRegistration(setOrgExistsModal);

  const queryClient = useQueryClient()
  const initData = queryClient.getQueryData(['initialData'])
  const states = initData?.us_state || [];
  
  const {register,handleSubmit,watch,formState: { errors, isSubmitting },} = useForm({
    mode: "onBlur",
  });

  const password = watch("password");

  const onSubmit = async (data) => {
    try {

      const selectedState = states.find(
        (state)=> String(state.id) === data.state
      )

      const payload = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.confirmPassword,
        phone_number: data.phoneNumber,
        state: selectedState?.id,

        name: data.orgName,
        addr_line1: data.addressLine1,
        addr_line2: data.addressLine2 || '',
        city: data.city,
        state_code: selectedState?.state_code,
        zip_code: data.zipCode

      }

      console.log("Register payload:", payload);
      registerUser(payload,{
        onSuccess: async()=>{
          await queryClient.prefetchQuery({
            queryKey: ["initData"],
            queryFn: fetchInitDataFn,
          })
        }
      })
    } catch (err) {
      console.error("Registration failed:", err);
    }
  };

  return (
    <React.Fragment>
      <div className="account-pages">
        <div
          className="accountbg"
          style={{
            background: `url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            minHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "24px",
          }}
        >
          <Card
            style={{
              // width: "900px",
              maxWidth: "100%",
              padding: "30px",
              borderRadius: "15px",
              border: "1.5px solid white",
              backgroundColor: "white",
              boxShadow: "0 0 15px rgba(0,0,0,.2)",
            }}
          >
            <Container fluid>
              <h4 style={{ fontWeight: 700, marginBottom: "20px" }}>
                Fill in your details
              </h4>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label htmlFor='firstName' className='form-label'>First Name</Label>
                      <input
                        id="firstName"
                        className='form-control border-primary-subtle'
                        placeholder="First Name"
                        name="firstName"
                        {...register("firstName", { required: "First name is required" })}
                      />
                      {errors.firstName && <span className="text-danger">{errors.firstName.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label htmlFor='lastName' className='form-label'>Last Name</Label>
                      <input
                        id="lastName"
                        placeholder="Last Name"
                        className='form-control border-primary-subtle'
                        name="lastName"
                        {...register("lastName", { required: "Last name is required" })}
                      />
                      {errors.lastName && <span className="text-danger">{errors.lastName.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label htmlFor='orgName' className='form-label'>Name of Organization</Label>
                      <input
                        id="orgName"
                        placeholder="Name of Organization"
                        className='form-control border-primary-subtle'
                        name="orgName"
                        {...register("orgName", { required: "Organization name is required" })}
                      />
                      {errors.orgName && <span className="text-danger">{errors.orgName.message}</span>}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Label htmlFor='addressLine1' className='form-label'>Address Line 1</Label>
                      <input
                        id="addressLine1"
                        placeholder="Address Line 1"
                        className='form-control border-primary-subtle'
                        name="addressLine1"
                        {...register("addressLine1", { required: "Address is required" })}
                      />
                      {errors.addressLine1 && <span className="text-danger">{errors.addressLine1.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label htmlFor='addressLine2' className='form-label'>Address Line 2</Label>
                      <input
                        id="addressLine2"
                        placeholder="Address Line 2"
                        className='form-control border-primary-subtle'
                        name="addressLine2"
                        {...register("addressLine2")}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label htmlFor='state' className='form-label'>State</Label>
                      <select
                        id="state"
                        type="select"
                        className='form-select border-primary-subtle'
                        name="state"
                        defaultValue=''
                        {...register("state", { required: "Required" })}
                      >
                        <option value="" disabled>Select State</option>
                        {states.map((state,index) => (
                          <option key={index} value={state.id}>
                            {state.state_code}
                          </option>
                        ))}
                      </select>
                    {errors.state && <span className="text-danger">{errors.state.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label htmlFor='city' className='form-label'>City</Label>
                      <input
                        id="city"
                        placeholder="City"
                        className='form-control border-primary-subtle'
                        name="city"
                        {...register("city", { required: "Required" })}
                      />
                      {errors.city && <span className="text-danger">{errors.city.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label htmlFor='zipCode' className='form-label'>Zip Code</Label>
                      <input
                        id="zipCode"
                        placeholder="Zip Code"
                        className='form-control border-primary-subtle'
                        name="zipCode"
                        invalid={!!errors.zipCode}
                        {...register("zipCode", {
                          required: "Required",
                          pattern: { value: /^\d{5}(-\d{4})?$/, message: "Invalid zip" },
                        })}
                      />
                      {errors.zipCode && <span className="text-danger">{errors.zipCode.message}</span>}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Label htmlFor='email' className='form-label'>Email</Label>
                      <input
                        id="email"
                        type="email"
                        placeholder="Email"
                        name="email"
                        className='form-control border-primary-subtle'
                        invalid={!!errors.email}
                        {...register("email", {
                          required: "Email is required",
                          setValueAs:(value)=> value?.trim().toLowerCase(),
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Invalid email address",
                          },
                        })}
                      />
                      {errors.email && <span className="text-danger">{errors.email.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label htmlFor='password' className='form-label'>Password</Label>
                      <input
                        id="password"
                        type="password"
                        name="password"
                        placeholder="Password"
                        invalid={!!errors.password}
                        className='form-control border-primary-subtle'
                        {...register("password", {
                          required: "Password is required",
                          minLength:{value:6,message:"Password must be at least 6 characters long"},
                          maxLength:{value:15, message:"Password must not exceed 12 characters"},
                          pattern:{value:/^(?!.*\s)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]{6,15}$/,
                          message:"Password must be 6–12 characters long, include at least one uppercase letter, one lowercase letter, one number, one special character, and contain no spaces."}
                        })}
                      />
                      {errors.password && <span className="text-danger">{errors.password.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label htmlFor='confirmPassword' className='form-label'>Confirm Password</Label>
                      <input
                        id="confirmPassword"
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        invalid={!!errors.confirmPassword}
                        className='form-control border-primary-subtle'
                        {...register("confirmPassword", {
                          required: "Please confirm your password",
                          validate: (value) => value === password || "Passwords do not match",
                          minLength:{value:6,message:"Password must be at least 6 characters long"},
                          maxLength:{value:15, message:"Password must not exceed 12 characters"},
                          pattern:{value:/^(?!.*\s)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]{6,15}$/,
                          message:"Password must be 6–12 characters long, include at least one uppercase letter, one lowercase letter, one number, one special character, and contain no spaces."}
                        })}
                      />
                      {errors.confirmPassword && <span className="text-danger">{errors.confirmPassword.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label htmlFor='phoneNumber' className='form-label'>Phone Number</Label>
                      <input
                        id="phoneNumber"
                        placeholder="Phone Number"
                        className='form-control border-primary-subtle'
                        name="phoneNumber"
                        invalid={!!errors.phoneNumber}
                        {...register("phoneNumber",{
                          pattern:{value:/^\+?[1-9][0-9]{7,14}$/, message: "Valid phone number required"},
                          minLength:{value: 7, message:"Phone number must be atleast 8 digits long"},
                          maxLength:{value: 15, message: "Phone number cannot exceed 15 digits"}
                        })}
                      />
                      {errors.phoneNumber && <span className="text-danger">{errors.phoneNumber.message}</span>}
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup check style={{ marginTop: "10px", marginBottom: "24px" }}>
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    className='form-check-input border-primary-subtle'
                    {...register("agreeTerms", { required: "You must agree to continue" })}
                  />
                  <Label check for="agreeTerms" style={{ marginLeft: "6px" }}>
                    I agree to the Terms &amp; Conditions and authorize TrueFocus Automation to
                    create an auto-approved company account.
                  </Label>
                  {errors.agreeTerms && (
                    <div style={{ color: "#dc3545", fontSize: "0.8rem" }}>
                      {errors.agreeTerms.message}
                    </div>
                  )}
                </FormGroup>

                <Row>
                  <Col className="d-flex justify-content-center gap-3">
                    <Button
                      type="button"
                      outline
                      // color="secondary"
                      style={{ minWidth: "140px", backgroundColor:"grey"}}
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      color="info"
                      disabled={isPending}
                      style={{ minWidth: "140px", backgroundColor: "#00ADF0", border: "none" }}
                    >
                      {isPending ? "Submitting..." : "Submit"}
                    </Button>
                  </Col>
                </Row>
              </form>
            </Container>
          </Card>
        </div>
      </div>

      <Modal
        isOpen={orgExistsModal}
        toggle={() => setOrgExistsModal(false)}
        centered
      >
        <ModalHeader toggle={() => setOrgExistsModal(false)}>
            Organization Already Exists
        </ModalHeader>

        <ModalBody className="text-center py-3">
            <h5 className="fw-bold mb-3">
                An account for this Organization already exists.
            </h5>

            <p className="mb-0">
                Please contact your Organization Admin to request access or reach
                out to{" "}
                <a href="mailto:info@truefocusnow.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                    info@truefocusnow.com
                </a>{" "}
                for assistance.
            </p>
        </ModalBody>

        <ModalFooter className="justify-content-center">
            <Button
                color="primary"
                onClick={() => setOrgExistsModal(false)}
                style={{
                    backgroundColor: "#00ADF0",
                    border: "none",
                    minWidth: "120px"
                }}
            >
                OK
            </Button>
        </ModalFooter>
    </Modal>
    </React.Fragment>
  );
};

export default Register;